import { useInfiniteScroll, useReverseScroll } from '@ibsys/kit';
import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult } from '@tanstack/react-query';
import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Параметры бесконечной прокрутки с защитой от бесплодных повторов подгрузки.
 */
export type UseGuardedInfiniteScrollParams<TPage, TError = unknown> = {
  /** `fetchNextPage` из `useInfiniteQuery` */
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<InfiniteQueryObserverResult<InfiniteData<TPage>, TError>>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** Количество уже загруженных порций — `data?.pages.length` */
  pagesCount: number;
  /**
   * Смена значения возобновляет остановленную автоподгрузку: список начинается заново (поиск, фильтр).
   *
   * Только примитив — сравнение идёт по значению. Составной ключ из нескольких условий потребитель
   * сводит к строке сам (`${search}|${groupId}`): объект, собранный в теле рендера, был бы новым на
   * каждом рендере и снимал бы остановку сразу после её установки.
   */
  resetKey?: string | number | boolean | null;
  /** Отступ упреждающего срабатывания маркера, px */
  threshold?: number;
  /**
   * С какой стороны догружается лента: `reverse` — маркер в её начале (история чата). В этом
   * направлении хук ещё и удерживает место просмотра: порция встаёт над видимой частью.
   */
  direction?: 'forward' | 'reverse';
  /** Прокручиваемый контейнер списка */
  root?: RefObject<HTMLElement | null>;
};

/**
 * Бесконечная прокрутка, в которой попытка подгрузки не повторяется сама по себе.
 *
 * Наблюдатель маркера из `@ibsys/kit` пересоздаётся после каждой завершившейся попытки и при
 * создании сразу сообщает о пересечении. Поэтому попытка, не пополнившая список, повторялась бы
 * без пауз и без ограничения числа повторов, пока маркер остаётся в зоне упреждения.
 *
 * Здесь одновременно идёт не больше одной попытки (иначе повторное обращение отменило бы уже
 * идущее), а попытка, не пополнившая список при заявленном продолжении, останавливает
 * автоподгрузку до `resumeAutoLoad` или до смены `resetKey`.
 *
 * `isAutoLoadStopped` означает именно отказ, а не конец списка, поэтому потребитель показывает
 * по нему сообщение и повтор, не сверяясь дополнительно с `hasNextPage`.
 *
 * В обратном направлении место просмотра удерживается здесь же: порция встаёт над видимой частью
 * и сдвинула бы прочитанное вниз на свою высоту.
 */
export const useGuardedInfiniteScroll = <TPage, TError = unknown>({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  pagesCount,
  resetKey,
  threshold,
  direction,
  root,
}: UseGuardedInfiniteScrollParams<TPage, TError>) => {
  const [isAutoLoadStopped, setIsAutoLoadStopped] = useState(false);
  const isLoadingRef = useRef(false);
  const pagesCountRef = useRef(pagesCount);
  const resetKeyRef = useRef(resetKey);
  const fallbackRootRef = useRef<HTMLElement | null>(null);
  const scrollAnchorRef = useRef({ resetKey, pagesCount, topSlotKey: `${isFetchingNextPage}|${isAutoLoadStopped}` });
  const overflowAnchorRef = useRef<{ node: HTMLElement; previousValue: string } | null>(null);
  const { captureScrollHeight, restoreScrollPosition } = useReverseScroll(root ?? fallbackRootRef);

  useEffect(() => {
    pagesCountRef.current = pagesCount;
  }, [pagesCount]);

  useEffect(() => {
    if (resetKeyRef.current === resetKey) return;

    resetKeyRef.current = resetKey;
    setIsAutoLoadStopped(false);
  }, [resetKey]);

  const loadNextPage = useCallback(() => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    const pagesBefore = pagesCountRef.current;

    fetchNextPage({ cancelRefetch: false })
      .then((result) => {
        const pagesAfter = result.data?.pages.length ?? 0;
        // Отсутствие прироста — отказ только пока продолжение заявлено: исчерпанный список
        // приходит таким же результатом, и остановка на нём выдала бы отказ за конец списка.
        if (result.isError || (result.hasNextPage && pagesAfter <= pagesBefore)) {
          setIsAutoLoadStopped(true);
        }
      })
      .catch(() => {
        setIsAutoLoadStopped(true);
      })
      .finally(() => {
        isLoadingRef.current = false;
      });
  }, [fetchNextPage]);

  /**
   * Возврат контейнеру его исходного удержания. Контейнер принадлежит потребителю и живёт дольше
   * обратного направления: снятое здесь встроенное удержание иначе осталось бы снятым и после того,
   * как лента перестала расти вверх, — уже чужому коду и без его ведома.
   */
  const releaseOverflowAnchor = useCallback(() => {
    const held = overflowAnchorRef.current;
    if (!held) return;

    held.node.style.overflowAnchor = held.previousValue;
    overflowAnchorRef.current = null;
  }, []);

  useLayoutEffect(() => releaseOverflowAnchor, [releaseOverflowAnchor]);

  /**
   * Лента прирастает сверху, поэтому прирост её высоты добавляется к прокрутке в том же кадре, в
   * котором произошёл, — иначе прочитанное уехало бы вниз ровно на него.
   *
   * Встроенное удержание браузера при этом снимается: браузер держит место просмотра сам, и вместе
   * со здешним прирост учитывался бы дважды — лента уезжала бы к концу переписки. Единственным
   * оставлено своё: браузерное есть не везде и не действует у самого края ленты. Снятие обратимо:
   * прежнее значение контейнер получает назад, как только обратное направление ему не нужно.
   *
   * Сверху ленту наращивает и пришедшая порция, и блок над ней, которым потребитель показывает
   * загрузку и отказ, — учитываются оба, иначе место просмотра уезжало бы на высоту этого блока.
   *
   * Замер обновляется каждым кадром, а не в момент обращения за порцией: при непрерывной прокрутке
   * порции приходят одна за другой, и замер, взятый до первой из них, отставал бы от ленты — часть
   * прироста осталась бы неучтённой, и лента прыгала бы на порцию назад.
   *
   * Первая порция ленты не компенсируется: с неё лента только строится, а место просмотра в ней
   * назначает потребитель (история чата открывается на последних сообщениях).
   */
  useLayoutEffect(() => {
    if (direction !== 'reverse') {
      releaseOverflowAnchor();
      return;
    }

    const scrollRoot = root?.current;
    if (overflowAnchorRef.current?.node !== scrollRoot) releaseOverflowAnchor();
    if (scrollRoot && !overflowAnchorRef.current) {
      overflowAnchorRef.current = { node: scrollRoot, previousValue: scrollRoot.style.overflowAnchor };
      scrollRoot.style.overflowAnchor = 'none';
    }

    const anchor = scrollAnchorRef.current;
    const topSlotKey = `${isFetchingNextPage}|${isAutoLoadStopped}`;
    const hasGrownAbove =
      anchor.resetKey === resetKey &&
      anchor.pagesCount > 0 &&
      (pagesCount > anchor.pagesCount || topSlotKey !== anchor.topSlotKey);

    scrollAnchorRef.current = { resetKey, pagesCount, topSlotKey };

    if (hasGrownAbove) restoreScrollPosition();
    captureScrollHeight();
  });

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadNextPage,
    hasMore: hasNextPage && !isAutoLoadStopped,
    isLoading: isFetchingNextPage,
    threshold,
    direction,
    root,
  });

  const resumeAutoLoad = useCallback(() => {
    setIsAutoLoadStopped(false);
  }, []);

  return { sentinelRef, isAutoLoadStopped, resumeAutoLoad };
};
